import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FORMS_CONFIG } from '../forms-config.token';
import { SyncedWorkspace } from './workspace.service';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private config = inject(FORMS_CONFIG);
  private get base() { return `${this.config.apiUrl}/api`; }

  // ── Forms ──────────────────────────────────────────────────────────────
  listForms() {
    return this.http.get<any[]>(`${this.base}/forms`);
  }

  createForm(name: string) {
    return this.http.post<any>(`${this.base}/forms`, { name });
  }

  getForm(formId: string) {
    return this.http.get<any>(`${this.base}/forms/${formId}`);
  }

  renameForm(formId: string, name: string) {
    return this.http.patch<any>(`${this.base}/forms/${formId}`, { name });
  }

  changeFormStatus(formId: string, isActive: boolean) {
    return this.http.patch<any>(`${this.base}/forms/${formId}/status`, { isActive });
  }

  setDefaultForm(formId: string) {
    return this.http.patch<void>(`${this.base}/forms/${formId}/default`, {});
  }

  clearDefaultForm(formId: string) {
    return this.http.delete<void>(`${this.base}/forms/${formId}/default`);
  }

  updateNotificationSettings(formId: string, emails: string[], templateId?: string | null) {
    return this.http.patch<{ notificationEmails: string[]; notificationTemplateId: string | null }>(
      `${this.base}/forms/${formId}/notifications`,
      { emails, templateId }
    );
  }

  // ── Form Versions ───────────────────────────────────────────────────────
  getLatestVersion(formId: string) {
    return this.http.get<any>(`${this.base}/forms/${formId}/versions/latest`);
  }

  getPublishedVersion(formId: string) {
    return this.http.get<any>(`${this.base}/forms/${formId}/versions/published`);
  }

  listVersions(formId: string) {
    return this.http.get<any[]>(`${this.base}/forms/${formId}/versions`);
  }

  createVersion(formId: string, schemaJson: unknown, rulesJson?: unknown) {
    return this.http.post<any>(`${this.base}/forms/${formId}/versions`, {
      schemaJson,
      rulesJson,
    });
  }

  publishVersion(formId: string, versionId: string) {
    return this.http.post<any>(
      `${this.base}/forms/${formId}/versions/${versionId}/publish`,
      {}
    );
  }

  // ── Users & Workspaces ──────────────────────────────────────────────────
  syncUser() {
    return this.http.post<{
      user: any;
      profile: { id: string; email: string | null; firstName: string | null; lastName: string | null };
      workspaces: SyncedWorkspace[];
    }>(`${this.base}/users/me`, {});
  }

  getWorkspaces() {
    return this.http.get<SyncedWorkspace[]>(`${this.base}/workspaces`);
  }

  // ── Submissions ─────────────────────────────────────────────────────────
  submitForm(formId: string, data: unknown, name?: string, draftPublicId?: string) {
    return this.http.post<{ submissionId: string }>(
      `${this.base}/forms/${formId}/submit`,
      { data, name, draftPublicId }
    );
  }

  saveDraft(formId: string, data: unknown, name?: string, draftPublicId?: string) {
    return this.http.post<{ submissionId: string; name: string }>(
      `${this.base}/forms/${formId}/draft`,
      { data, name, draftPublicId }
    );
  }

  listSubmissions(limit = 20, offset = 0) {
    return this.http.get<any>(`${this.base}/submissions`, {
      params: { limit, offset },
    });
  }

  getSubmission(submissionId: string) {
    return this.http.get<any>(`${this.base}/submissions/${submissionId}`);
  }

  deleteSubmission(submissionId: string) {
    return this.http.delete<void>(`${this.base}/submissions/${submissionId}`);
  }

  downloadSubmissionPdf(submissionId: string) {
    return this.http.get(`${this.base}/submissions/${submissionId}/pdf`, { responseType: 'blob' });
  }

  // Returns distinct {fieldName, fieldType} pairs seen across all versions of a form
  getSubmissionFields(formId: string) {
    return this.http.get<{ fieldName: string; fieldType: string; label: string }[]>(
      `${this.base}/forms/${formId}/submissions/fields`
    );
  }

  getUploadToken() {
    return this.http.get<{ success: boolean; sasToken: string; accountName: string; containerName: string }>(
      `${this.base}/upload-photo/token`
    );
  }

  triggerDebugException() {
    return this.http.get(`${this.base}/debug/exception`);
  }

  analyzePdf(pdfBase64: string) {
    return this.http.post<any>(`${this.base}/analyze-pdf`, { pdfBase64 });
  }

  // Executes a dynamic filter query against the submission_facts OLAP table
  querySubmissions(
    conditions: unknown[],
    logic: 'AND' | 'OR',
    formId: string | null,
    limit = 20,
    offset = 0,
    submissionName?: string,
  ) {
    return this.http.post<any>(`${this.base}/submissions/query`, {
      conditions,
      logic,
      formId,
      limit,
      offset,
      submissionName: submissionName || null,
    });
  }
}
